import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { HOSPITAL } from "../data/hospital";
import type { ChapterData } from "../data/content-types";
import type { InteractableDef } from "../core/types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";
import { Sfx } from "../systems/Sfx";

/** CASO EXTRA — "Los primeros minutos". Enfermería: accidentado, botiquín, DESA, cuadro en tensión. */
export class HospitalScene extends WalkChapter {
  protected chapter: ChapterData = HOSPITAL;
  protected grid = { w: 15, h: 12 };
  protected spawn = { x: 8, y: 10 };
  protected worldSigns = [
    { id: "e_auxilios", x: 1.4, y: 0.3 },
    { id: "w_elec", x: 5.5, y: 0.3 },
    { id: "e_ducha", x: 9.5, y: 0.3 },
    { id: "e_salida", x: 13.4, y: 0.3 },
  ];
  private decor: Phaser.GameObjects.GameObject[] = [];
  private charcoGfx?: Phaser.GameObjects.Graphics;
  private sparkFx: Phaser.GameObjects.Arc[] = [];

  constructor() {
    super("hospital");
  }

  protected floorKind(x: number, y: number): P.TileKind {
    const h = (x * 928371 + y * 12347) & 255;
    return h % 20 === 0 ? "dirt" : "concrete";
  }

  /** Persona tendida en el suelo (inconsciente). */
  private downed(): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    const g = this.add.graphics();
    g.fillStyle(0x1a1206, 0.18); g.fillEllipse(0, 6, 46, 14); // sombra
    g.fillStyle(0x2f6b8f, 1); g.fillRoundedRect(-22, -8, 34, 14, 5); // torso tumbado
    g.fillStyle(0x24405a, 1); g.fillRoundedRect(8, -6, 18, 10, 4);   // piernas
    g.fillStyle(0xf0c89a, 1); g.fillCircle(-24, -4, 7);              // cabeza
    g.fillStyle(0xffd23b, 0.9); g.fillRect(-18, -6, 24, 4);          // banda alta visibilidad
    c.add(g);
    return c;
  }

  private medBox(base: number, cross: number): Phaser.GameObjects.GameObject[] {
    const crate = P.crate(this, base);
    const g = this.add.graphics();
    g.fillStyle(cross, 1);
    g.fillRect(-4, -24, 8, 2.5); g.fillRect(-1.2, -27, 2.5, 8); // cruz
    return [crate, g];
  }

  protected buildInteractableArt(def: InteractableDef): Phaser.GameObjects.GameObject[] {
    switch (def.id) {
      case "herido": return [this.downed()];
      case "botiquin": return this.medBox(0xf2f0ea, 0x2f8f57);
      case "desa": return this.medBox(0x2f8f57, 0xffffff);
      case "medidas": return [P.machine(this, 0xf0a91b)];
      default: return super.buildInteractableArt(def);
    }
  }

  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0xcdd6d2);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0xe0e8e4, 0xe0e8e4, 0xc4cec9, 0xc4cec9, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.58 + over);
    // banda sanitaria (verde) en la pared del fondo
    const band = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    band.fillStyle(0x2f8f57, 0.5); band.fillRect(-over, H * 0.34, W + over * 2, 8);
    band.fillStyle(0x2f8f57, 0.22); band.fillRect(-over, H * 0.34 + 8, W + over * 2, 20);
    this.buildWalls({ left: 0xc4cdc8, right: 0xd6ded9, height: 172, base: 0x9aa6a1, texture: "tile" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };

    // charco bajo el cuadro (peligro activo) junto al accidentado
    this.charcoGfx = undefined; this.sparkFx = [];
    if (!this.state.chapter("hospital").done) {
      const g = this.add.graphics().setDepth(-9500);
      const p = this.iso(6.4, 5.2), cy = p.sy + TILE_H / 2;
      g.fillStyle(0x6a86a0, 0.4); g.fillEllipse(p.sx, cy, 46, 20);
      g.fillStyle(0x8fb0c8, 0.3); g.fillEllipse(p.sx - 4, cy - 2, 24, 10);
      this.decor.push(g); this.charcoGfx = g;
    }

    // mobiliario de enfermería
    put(P.labbench(this), 2.4, 1.6);
    put(P.labsink(this), 4.4, 1.5);
    put(P.watercooler(this), 12.6, 1.6);
    put(P.filecabinet(this), 1.6, 4.4);
    put(P.shelving(this), 13.2, 6.4);
    put(P.wastebin(this), 9.6, 3.4);
    put(P.plant(this), 2.2, 9.2);
    put(P.plant(this), 12.8, 9.2);
    put(P.officechair(this), 3.4, 5.4);
    put(P.extinguisher(this), 1.4, 6.6);
    put(P.cone(this), 6.2, 6.8);

    // camilla improvisada (labbench baja) cerca del accidentado — contexto
    put(P.crate(this, 0xdfe6e2), 9.2, 5.6);

    // luminarias de techo (luz clínica) + charco de luz suave
    for (const [lx, ly] of [[4, 3], [8, 3], [12, 3], [6.5, 6.5], [10.5, 6.5]] as [number, number][]) {
      const p = this.iso(lx, ly);
      const pg = this.add.graphics().setDepth(-9300); const pcy = p.sy + TILE_H / 2;
      pg.fillStyle(0xffffff, 0.16); pg.fillEllipse(p.sx, pcy, 84, 38);
      this.decor.push(pg); this.addPulse(pg, 0.08, 0.003, 0.9);
      const lamp = P.hanglamp(this, 150); lamp.setPosition(p.sx, p.sy).setDepth(50000); this.decor.push(lamp); this.addSway(lamp);
    }

    // chispa del cuadro que causó el accidente (7,4)
    if (!this.state.chapter("hospital").done) {
      this.sparkFx = this.addRisers(7, 4, { count: 3, color: 0xfff2a0, alpha: 0.85, r: 1.3, rise: 14, sp: 0.03, grow: 0.2, dyBase: -8, depth: 44000 });
    }

    if (this.state.chapter("hospital").done) this.onResolved();
  }

  /** Efectos en el mundo al actuar: cortar la corriente y llamar a emergencias. */
  protected attendedEffect(ref: string, live: boolean): void {
    if (ref === "escena") this.cutPower(live);
    if (ref === "herido") this.callAmbulance(live);
  }

  private cutPower(live: boolean): void {
    this.sparkFx.forEach((c) => c.destroy()); this.sparkFx = [];
    if (this.charcoGfx) {
      if (live) this.tweens.add({ targets: this.charcoGfx, alpha: 0, duration: 500, onComplete: () => this.charcoGfx?.destroy() });
      else this.charcoGfx.destroy();
      this.charcoGfx = undefined;
    }
    const p = this.iso(7, 4);
    this.add.text(p.sx, p.sy - 40, "SIN TENSIÓN", { fontSize: "9px", color: "#fff", backgroundColor: "#2f8f57", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
  }

  /** Dibuja una ambulancia (mirando a la izquierda). */
  private ambulance(): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    const g = this.add.graphics();
    g.fillStyle(0x1a1206, 0.2); g.fillEllipse(0, 6, 66, 16);
    g.fillStyle(0xf3f4f6, 1); g.fillRoundedRect(-34, -34, 68, 30, 5);       // caja
    g.fillStyle(0xe9edf0, 1); g.fillRoundedRect(-34, -20, 68, 16, 3);       // banda baja
    g.fillStyle(0xd23b3b, 1); g.fillRect(-34, -22, 68, 5);                  // franja roja
    g.fillStyle(0xd23b3b, 1); g.fillRect(-26, -30, 8, 2.5); g.fillRect(-23, -33, 2.5, 8); // cruz
    g.fillStyle(0xbfe0ee, 1); g.fillRoundedRect(-33, -32, 12, 12, 2);       // luna delantera
    g.fillStyle(0x2b3038, 1); g.fillCircle(-20, -3, 6); g.fillCircle(20, -3, 6); // ruedas
    g.fillStyle(0x0d0f12, 1); g.fillCircle(-20, -3, 2.5); g.fillCircle(20, -3, 2.5);
    c.add(g);
    const light = this.add.circle(6, -40, 4, 0x3a7bd8, 1).setName("light");
    c.add(light);
    return c;
  }

  private callAmbulance(live: boolean): void {
    const herido = this.itemRoot("herido");
    if (!live) { herido?.setVisible(false); return; }
    Sfx.good();
    this.ui.toast("112", "Emergencias en camino", "warn");
    const amb = this.ambulance();
    const entry = this.iso(14.5, 10.5), near = this.iso(9.2, 6.4), exit = this.iso(15.5, 3.5);
    amb.setPosition(entry.sx, entry.sy).setDepth(80000);
    const light = amb.getByName("light") as Phaser.GameObjects.Arc;
    if (light) this.tweens.add({ targets: light, alpha: 0.2, yoyo: true, repeat: -1, duration: 240 });
    this.tweens.add({
      targets: amb, x: near.sx, y: near.sy, duration: 1500, ease: "Sine.easeInOut",
      onComplete: () => {
        this.time.delayedCall(1000, () => {
          this.ui.toast("Atendido", "El herido va camino del hospital", "good");
          if (herido) this.tweens.add({ targets: herido, alpha: 0, y: herido.y - 8, duration: 500, onComplete: () => herido.setVisible(false) });
          this.time.delayedCall(1100, () => {
            this.tweens.add({ targets: amb, x: exit.sx, y: exit.sy, alpha: 0, duration: 1700, ease: "Sine.easeIn", onComplete: () => amb.destroy() });
          });
        });
      },
    });
  }

  protected onResolved(): void {
    const c = this.iso(7, 4);
    this.add.text(c.sx, c.sy - 40, "SIN TENSIÓN", { fontSize: "9px", color: "#fff", backgroundColor: "#2f8f57", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
    const d = this.iso(12, 4);
    this.add.text(d.sx, d.sy - 40, "DESA SEÑALIZADO", { fontSize: "9px", color: "#fff", backgroundColor: "#2f8f57", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
  }
}
